/**
 * VestingChart.js - Time-Series Chart Component
 * Displays token distribution timeline with Recharts
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart
} from 'recharts';
import {
  Calendar,
  Coins,
  FileText,
  TrendingUp,
  Zap,
  Lock,
  Unlock,
  Rocket,
  CheckCircle2
} from 'lucide-react';

import { mockVestingData, mockProjections } from '../utils/mockVestingData';
import { formatDate, formatTokenAmount } from '../utils/vestingHelpers';

import './VestingChart.css';

const VestingChart = ({ data, state, useMockData = true }) => {
  const [chartData, setChartData] = useState([]);
  const [isAnimated, setIsAnimated] = useState(false);

  // Process chart data
  useEffect(() => {
    const processedData = useMockData 
      ? processVestingData(mockVestingData, mockProjections)
      : processVestingData(data?.earningsHistory || [], []);
    
    setChartData(processedData);
    
    // Trigger animation after data loads
    setTimeout(() => setIsAnimated(true), 100);
  }, [data, useMockData]);

  // Process and merge vesting data with projections
  const processVestingData = (vestingData, projections) => {
    // Combine actual data with projections
    const combined = [...vestingData];
    
    // Add projection data points
    projections.forEach(proj => {
      if (!combined.find(d => d.date === proj.date)) {
        combined.push({
          ...proj,
          projected: proj.projected,
          cumulative: null // Actual cumulative stays null for future dates
        });
      }
    });

    // Sort by date
    return combined.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // Find TGE and vesting end dates
  const milestones = useMemo(() => {
    const tgeEntry = chartData.find(d => d.isTGE);
    const vestingEndEntry = chartData.find(d => d.isVestingEnd);
    
    return {
      tgeDate: tgeEntry?.date || '2025-11-27',
      vestingEndDate: vestingEndEntry?.date || '2026-05-27'
    };
  }, [chartData]);

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0]?.payload;
    if (!data) return null;

    return (
      <div className="chart-tooltip">
        <div className="tooltip-header">
          <span className="tooltip-date">
            <Calendar size={14} className="tooltip-icon" />
            {formatDate(label)}
          </span>
        </div>
        <div className="tooltip-content">
          {data.received && (
            <div className="tooltip-row">
              <span className="tooltip-label">
                <Coins size={14} className="tooltip-icon" />
                Tokens Received:
              </span>
              <span className="tooltip-value">{data.received.toLocaleString()} TPHILE</span>
            </div>
          )}
          {data.source && (
            <div className="tooltip-row">
              <span className="tooltip-label">
                <FileText size={14} className="tooltip-icon" />
                Source:
              </span>
              <span className="tooltip-value source">{data.source}</span>
            </div>
          )}
          {(data.cumulative || data.projected) && (
            <div className="tooltip-row cumulative">
              <span className="tooltip-label">
                <TrendingUp size={14} className="tooltip-icon" />
                Cumulative:
              </span>
              <span className="tooltip-value">{(data.cumulative || data.projected).toLocaleString()} TPHILE</span>
            </div>
          )}
          {data.type && (
            <div className={`tooltip-badge ${data.type}`}>
              {data.type === 'instant' && <><Zap size={12} /> Instant</>}
              {data.type === 'vested' && <><Lock size={12} /> Vested</>}
              {data.type === 'unlock' && <><Unlock size={12} /> Unlocked</>}
            </div>
          )}
        </div>
        {data.isTGE && (
          <div className="tooltip-milestone">
            <Rocket size={14} />
            TGE Triggered
          </div>
        )}
        {data.isVestingEnd && (
          <div className="tooltip-milestone success">
            <CheckCircle2 size={14} />
            Vesting Complete
          </div>
        )}
      </div>
    );
  };

  // Custom Legend Component
  const CustomLegend = ({ payload }) => {
    return (
      <div className="chart-legend">
        <div className="legend-item">
          <span className="legend-line solid" />
          <span>Received Tokens</span>
        </div>
        <div className="legend-item">
          <span className="legend-line dashed" />
          <span>Future Projections</span>
        </div>
      </div>
    );
  };

  // Format Y-axis ticks
  const formatYAxis = (value) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value;
  };

  // Format X-axis dates
  const formatXAxis = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // If in pre-TGE state, show estimated unlock visualization
  if (state === 'pre-tge') {
    return (
      <div className="vesting-chart-container pre-tge">
        <div className="pre-tge-chart">
          <div className="pre-tge-visual">
            <div className="token-stack">
              <div className="token-section instant">
                <div className="section-label">50% Instant at TGE</div>
                <div className="section-value">
                  {formatTokenAmount(data?.estimatedUnlock?.instantAmount || 2600)} TPHILE
                </div>
              </div>
              <div className="token-section vested">
                <div className="section-label">50% Vested (6 months)</div>
                <div className="section-value">
                  {formatTokenAmount(data?.estimatedUnlock?.vestedAmount || 2600)} TPHILE
                </div>
              </div>
            </div>
            <div className="timeline-preview">
              <div className="timeline-point tge">
                <div className="point-marker" />
                <span>TGE</span>
              </div>
              <div className="timeline-line" />
              <div className="timeline-point end">
                <div className="point-marker" />
                <span>+6 months</span>
              </div>
            </div>
          </div>
          <p className="pre-tge-note">
            Chart will display real-time token distribution once TGE is triggered
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`vesting-chart-container ${isAnimated ? 'animated' : ''}`}>
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <defs>
            <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#FFD700" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#FFD700" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke="rgba(255, 215, 0, 0.1)"
            vertical={false}
          />

          <XAxis
            dataKey="date"
            stroke="#FFD700"
            tick={{ fill: '#FFD700', fontSize: 12 }}
            tickFormatter={formatXAxis}
            axisLine={{ stroke: 'rgba(255, 215, 0, 0.3)' }}
            tickLine={{ stroke: 'rgba(255, 215, 0, 0.3)' }}
            interval="preserveStartEnd"
          />

          <YAxis
            stroke="#FFD700"
            tick={{ fill: '#FFD700', fontSize: 12 }}
            tickFormatter={formatYAxis}
            axisLine={{ stroke: 'rgba(255, 215, 0, 0.3)' }}
            tickLine={{ stroke: 'rgba(255, 215, 0, 0.3)' }}
            label={{
              value: 'TPHILE Tokens',
              angle: -90,
              position: 'insideLeft',
              fill: '#FFD700',
              fontSize: 13,
              dy: 40
            }}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} />

          {/* TGE Reference Line */}
          <ReferenceLine
            x={milestones.tgeDate}
            stroke="#FFD700"
            strokeDasharray="5 5"
            label={{
              value: 'TGE',
              position: 'top',
              fill: '#FFD700',
              fontSize: 12
            }}
          />

          {/* Vesting Complete Reference Line */}
          {state !== 'pre-tge' && (
            <ReferenceLine
              x={milestones.vestingEndDate}
              stroke="#32CD32"
              strokeDasharray="5 5"
              label={{
                value: 'Vesting Complete',
                position: 'top',
                fill: '#32CD32',
                fontSize: 12
              }}
            />
          )}

          {/* Area fill under the line */}
          <Area
            type="monotone"
            dataKey="cumulative"
            fill="url(#goldGradient)"
            stroke="none"
          />

          {/* Actual cumulative line */}
          <Line
            type="monotone"
            dataKey="cumulative"
            stroke="#FFD700"
            strokeWidth={3}
            dot={{
              fill: '#FFD700',
              stroke: '#000',
              strokeWidth: 2,
              r: 5
            }}
            activeDot={{
              r: 8,
              fill: '#FFD700',
              stroke: '#fff',
              strokeWidth: 2
            }}
            animationDuration={1500}
            animationEasing="ease-out"
            connectNulls={false}
          />

          {/* Projected line (dashed) */}
          <Line
            type="monotone"
            dataKey="projected"
            stroke="#FFD700"
            strokeWidth={2}
            strokeDasharray="8 4"
            dot={{
              fill: '#1a1a1a',
              stroke: '#FFD700',
              strokeWidth: 2,
              r: 4
            }}
            opacity={0.6}
            animationDuration={1500}
            animationEasing="ease-out"
          />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Chart annotations */}
      <div className="chart-annotations">
        <div className="annotation tge">
          <span className="annotation-dot" style={{ background: '#FFD700' }} />
          <span>TGE: 50% Instant Release</span>
        </div>
        <div className="annotation vesting">
          <span className="annotation-dot" style={{ background: '#32CD32' }} />
          <span>Vesting: 50% over 6 months</span>
        </div>
      </div>
    </div>
  );
};

export default VestingChart;

